import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Select, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type FileEntryCondition,
  type SelectOption,
  type WorkflowField,
  type WorkflowStep,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { ICON_BTN_PROPS } from '@/components/iconButton';

import { type ConditionScope, eligibleSourceFields } from './sourceFields';

const { Text } = Typography;

// Slightly smaller icon button — matches the in-row gear/✕ on the PDF rows so
// the visual rhythm is the same everywhere this modal renders.
const iconBtn = {
  ...ICON_BTN_PROPS,
  style: { ...ICON_BTN_PROPS.style, width: 26, height: 26, fontSize: 12, borderRadius: 6 },
} as const;

export type ConditionOperator =
  'equals' | 'not_equals' | 'is_true' | 'is_false' | 'contains' | 'not_contains';

/** Which operators are valid for a given source-field type. */
export function operatorsFor(field: WorkflowField | null | undefined): ConditionOperator[] {
  if (!field) return [];
  if (field.type === 'Toggle' || field.type === 'Checkbox') return ['is_true', 'is_false'];
  // CheckboxGroup holds an array of option values — the only sensible
  // operators are "the array contains X" / "the array doesn't contain X".
  // Equals/not_equals would stringify the array and compare against a
  // single value, which never matches.
  if (field.type === 'CheckboxGroup') return ['contains', 'not_contains'];
  return ['equals', 'not_equals'];
}

/**
 * Modal for editing a list of gating conditions + a shared all/any combinator.
 * Pure-controlled: parent owns persistence and passes `onSave`. Used by
 * the PDF entries drawer (gate which PDFs get populated), the field
 * properties panel (gate when a field is visible), and the canvas section
 * header (gate when a section is visible).
 *
 * The modal is open when `open=true` AND `allSteps` has at least one
 * DynamicForm step with an eligible field; otherwise the picker is disabled.
 *
 * `scope` defaults to PDF (every step, every eligible field). Field/section/
 * step callers MUST pass an explicit scope so the picker narrows correctly.
 */
export function ConditionsEditorModal({
  open,
  title,
  intro,
  initialConditions,
  initialCombinator,
  allSteps,
  scope = { kind: 'pdf' },
  saving,
  onCancel,
  onSave,
}: {
  open: boolean;
  /** Modal header text. Pickers vary per caller (e.g. "Populate this PDF
   *  only if…" vs "Show this field only if…"). */
  title: string;
  /** One-line description that pairs with the modal title. */
  intro: string;
  initialConditions: FileEntryCondition[];
  initialCombinator: 'all' | 'any' | null;
  allSteps: WorkflowStep[];
  /** See `ConditionScope` — narrows the source-field picker. */
  scope?: ConditionScope;
  saving?: boolean;
  onCancel: () => void;
  /** Caller persists; modal closes on success. Empty list + null combinator
   *  clears the gating. */
  onSave: (
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const I18N_NS = 'workflow.conditionsEditor';

  // Selectable source fields, grouped by owning DynamicForm step (cascading
  // Step → Field selects keep the modal navigable at scale). The scope filter
  // is applied during the walk, so the picker never even reveals a field this
  // callsite isn't allowed to gate on — see `ConditionScope` for the rules.
  const stepsWithFields = useMemo(
    () => eligibleSourceFields(allSteps, scope, isFr),
    [allSteps, isFr, scope],
  );

  const [draftConditions, setDraftConditions] = useState<FileEntryCondition[]>([]);
  const [combinator, setCombinator] = useState<'all' | 'any'>('all');

  // The "add a new condition" inline picker state.
  const [pickStepId, setPickStepId] = useState<string | null>(null);
  const [pickFieldId, setPickFieldId] = useState<string | null>(null);
  const [pickOperator, setPickOperator] = useState<ConditionOperator | null>(null);
  const [pickValue, setPickValue] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftConditions(initialConditions);
    // Default combinator to "all" — most multi-condition rules are AND in
    // practice; originator can flip to "any" once there are 2+ rules.
    setCombinator(initialCombinator ?? 'all');
    setPickStepId(null);
    setPickFieldId(null);
    setPickOperator(null);
    setPickValue(null);
    // initialConditions / initialCombinator intentionally excluded from deps
    // so reopening the modal with a different reference doesn't wipe an
    // in-progress draft mid-edit. We only re-seed on open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickStep = stepsWithFields.find((s) => s.stepId === pickStepId) ?? null;
  const pickField = pickStep?.fields.find((f) => f.id === pickFieldId) ?? null;
  const pickOperators = operatorsFor(pickField);
  const pickValueOptions: SelectOption[] | null = (() => {
    if (!pickField) return null;
    if (pickField.type === 'Toggle' || pickField.type === 'Checkbox') return null;
    return pickField.options;
  })();

  const pickComplete =
    pickStepId !== null &&
    pickFieldId !== null &&
    pickOperator !== null &&
    (pickOperator === 'is_true' ||
      pickOperator === 'is_false' ||
      (pickValue !== null && pickValue !== ''));

  const addPickToList = () => {
    if (!pickComplete || !pickStepId || !pickFieldId || !pickOperator) return;
    const next: FileEntryCondition = {
      fieldStepId: pickStepId,
      fieldId: pickFieldId,
      operator: pickOperator,
      // Booleans pack the operator name in `value` so the BE store always
      // has a non-null string per-condition.
      value:
        pickOperator === 'is_true' || pickOperator === 'is_false'
          ? pickOperator
          : (pickValue ?? ''),
    };
    setDraftConditions((prev) => [...prev, next]);
    setPickStepId(null);
    setPickFieldId(null);
    setPickOperator(null);
    setPickValue(null);
  };

  const removeCondition = (index: number) => {
    setDraftConditions((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Read-only summary line for a saved condition row. Walks the FULL workflow
   * (`allSteps`) — not the scope-filtered `stepsWithFields` — so a condition
   * whose source field still exists OUTSIDE the current picker scope still
   * resolves to its real labels. Without this, saved rules referencing
   * fields outside the new scope would mislabel themselves as "(deleted)"
   * and tempt the originator to delete a perfectly-valid rule.
   */
  const summarise = (c: FileEntryCondition): string => {
    const step = allSteps.find((s) => s.id === c.fieldStepId);
    const stepLabel = step
      ? (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr
      : t(`${I18N_NS}.missingRef`);
    let field: WorkflowField | undefined;
    if (step?.type === 'DynamicForm') {
      for (const sec of step.sections) {
        for (const row of sec.rows) {
          for (const col of row.columns) {
            if (col.field?.id === c.fieldId) field = col.field;
          }
        }
      }
    }
    const fieldLabel = field
      ? (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr
      : t(`${I18N_NS}.missingRef`);
    const opLabel = t(`${I18N_NS}.op.${c.operator}`);
    const valueLabel =
      c.operator === 'is_true' || c.operator === 'is_false'
        ? ''
        : (() => {
            const opt = field?.options.find((o) => o.value === c.value);
            return opt
              ? ` ${(isFr ? opt.labelFr : opt.labelEn) || opt.labelEn || opt.labelFr}`
              : ` ${c.value}`;
          })();
    return `${stepLabel} — ${fieldLabel} ${opLabel}${valueLabel}`;
  };

  const handleSave = () => {
    // Auto-commit any complete in-progress rule before persisting. Without
    // this, originators who fill in the picker and click Save (instead of
    // Add rule → Save) lost their typed rule silently. We can't call
    // addPickToList() and then read draftConditions in the same handler
    // (setState is async), so we merge the in-flight pick into a local
    // copy before handing off to onSave.
    let finalConditions = draftConditions;
    if (pickComplete && pickStepId && pickFieldId && pickOperator) {
      const inProgress: FileEntryCondition = {
        fieldStepId: pickStepId,
        fieldId: pickFieldId,
        operator: pickOperator,
        value:
          pickOperator === 'is_true' || pickOperator === 'is_false'
            ? pickOperator
            : (pickValue ?? ''),
      };
      finalConditions = [...draftConditions, inProgress];
    }
    void onSave(finalConditions, finalConditions.length === 0 ? null : combinator);
  };

  return (
    <BmoModal
      open={open}
      title={title}
      onCancel={onCancel}
      width={620}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" loading={!!saving} onClick={handleSave}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {intro}
        </Text>

        {/* Combinator — hidden until you have 2+ rules; without two things
            to combine the toggle is moot. */}
        {draftConditions.length >= 2 && (
          <div>
            <label style={{ fontSize: 12, color: '#6b7280' }}>
              {t(`${I18N_NS}.combinatorLabel`)}
            </label>
            <Select<'all' | 'any'>
              style={{ width: '100%' }}
              value={combinator}
              onChange={setCombinator}
              options={[
                { value: 'all', label: t(`${I18N_NS}.combinatorAll`) },
                { value: 'any', label: t(`${I18N_NS}.combinatorAny`) },
              ]}
            />
          </div>
        )}

        {draftConditions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, color: '#6b7280' }}>{t(`${I18N_NS}.listLabel`)}</label>
            {draftConditions.map((c, i) => (
              <div
                key={`${c.fieldId}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fff',
                }}
              >
                <span style={{ flex: 1, fontSize: 13, color: '#111827' }}>{summarise(c)}</span>
                <Tooltip title={t(`${I18N_NS}.remove`)}>
                  <Button
                    {...iconBtn}
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeCondition(i)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        )}

        {/* Inline picker — Step → Field → Operator → Value. The "Add" button
            stays disabled until all four are picked. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 12,
            border: '1px dashed #cbd5e1',
            borderRadius: 8,
            background: '#f8fafc',
          }}
        >
          <Text strong style={{ fontSize: 12 }}>
            {t(`${I18N_NS}.addTitle`)}
          </Text>
          {/* No eligible source fields for this scope — tell the user why
              instead of just leaving them with a greyed-out dropdown. */}
          {stepsWithFields.length === 0 && (
            <div
              style={{
                fontSize: 12,
                color: '#92400e',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: 6,
                padding: '6px 8px',
              }}
            >
              {t(`${I18N_NS}.emptyForScope.${scope.kind}`)}
            </div>
          )}
          <Select<string>
            style={{ width: '100%' }}
            value={pickStepId ?? undefined}
            placeholder={t(`${I18N_NS}.stepPlaceholder`)}
            disabled={stepsWithFields.length === 0}
            onChange={(next) => {
              setPickStepId(next);
              setPickFieldId(null);
              setPickOperator(null);
              setPickValue(null);
            }}
            showSearch
            optionFilterProp="label"
            options={stepsWithFields.map((s) => ({ value: s.stepId, label: s.stepTitle }))}
            notFoundContent={t(`${I18N_NS}.noFields`)}
          />
          <Select<string>
            style={{ width: '100%' }}
            value={pickFieldId ?? undefined}
            placeholder={t(`${I18N_NS}.fieldPlaceholder`)}
            disabled={!pickStep}
            onChange={(next) => {
              setPickFieldId(next);
              setPickOperator(null);
              setPickValue(null);
            }}
            showSearch
            optionFilterProp="label"
            options={(pickStep?.fields ?? []).map((field) => ({
              value: field.id,
              label: (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr,
            }))}
          />
          <Select<ConditionOperator>
            style={{ width: '100%' }}
            value={pickOperator ?? undefined}
            placeholder={t(`${I18N_NS}.operatorPlaceholder`)}
            disabled={!pickField}
            onChange={(op) => {
              setPickOperator(op);
              if (op === 'is_true' || op === 'is_false') setPickValue(null);
            }}
            options={pickOperators.map((op) => ({
              value: op,
              label: t(`${I18N_NS}.op.${op}`),
            }))}
          />
          {pickValueOptions && (
            <Select<string>
              style={{ width: '100%' }}
              value={pickValue ?? undefined}
              placeholder={t(`${I18N_NS}.valuePlaceholder`)}
              disabled={!pickOperator}
              onChange={setPickValue}
              options={pickValueOptions.map((o) => ({
                value: o.value,
                label: (isFr ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr,
              }))}
            />
          )}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            disabled={!pickComplete}
            onClick={addPickToList}
          >
            {t(`${I18N_NS}.addButton`)}
          </Button>
        </div>
      </div>
    </BmoModal>
  );
}
